import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const key = "cero-ai.google-analytics-consent.v1";
type Choice = "unknown" | "granted" | "denied";
type GoogleWindow = Window & { dataLayer?: unknown[][]; gtag?: (...args: unknown[]) => void };
let loaded = false;

function measurementId() {
  return document.querySelector<HTMLMetaElement>('meta[name="google-analytics-id"]')?.content ?? "";
}

function start(id: string) {
  if (loaded || !/^G-[A-Z0-9]+$/.test(id)) return;
  loaded = true;
  const googleWindow = window as GoogleWindow;
  googleWindow.dataLayer ??= [];
  googleWindow.gtag = (...args: unknown[]) => googleWindow.dataLayer!.push(args);
  googleWindow.gtag("consent", "default", {
    analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied",
  });
  googleWindow.gtag("js", new Date());
  googleWindow.gtag("consent", "update", { analytics_storage: "granted" });
  googleWindow.gtag("config", id, { send_page_view: false, page_location: `${window.location.origin}${window.location.pathname}` });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);
}

export function GoogleAnalyticsConsent() {
  const id = measurementId();
  const [choice, setChoice] = useState<Choice | null>(null);
  const [open, setOpen] = useState(false);
  const [footer, setFooter] = useState<HTMLElement | null>(null);
  const german = document.documentElement.lang === "de";

  useEffect(() => {
    let saved: string | null = null;
    try { saved = window.localStorage.getItem(key); } catch { /* keep the choice for this page only */ }
    setChoice(saved === "granted" || saved === "denied" ? saved : "unknown");
  }, []);

  useEffect(() => {
    const update = () => {
      const target = document.querySelector<HTMLElement>("[data-consent-footer]") ?? document.querySelector<HTMLElement>("footer");
      setFooter(current => current === target ? current : target);
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (choice !== "granted" || !id) return;
    start(id);
    const pageView = () => (window as GoogleWindow).gtag?.("event", "page_view", {
      send_to: id, page_location: `${window.location.origin}${window.location.pathname}`,
    });
    pageView();
    window.addEventListener("popstate", pageView);
    return () => window.removeEventListener("popstate", pageView);
  }, [choice, id]);

  function choose(next: "granted" | "denied") {
    try { window.localStorage.setItem(key, next); } catch { /* keep the choice for this page only */ }
    if (next === "denied" && choice === "granted") {
      (window as GoogleWindow).gtag?.("consent", "update", { analytics_storage: "denied" });
      window.location.reload();
      return;
    }
    setChoice(next);
    setOpen(false);
  }

  if (!/^G-[A-Z0-9]+$/.test(id) || choice === null) return null;
  return (
    <>
      {choice !== "unknown" && footer && createPortal(
        <button type="button" onClick={() => setOpen(true)} aria-label={german ? `Cookies verwalten, Analyse ${choice === "granted" ? "an" : "aus"}` : `Manage cookies, analytics ${choice === "granted" ? "on" : "off"}`} style={footerButtonStyle}>{german ? "Cookies verwalten" : "Manage cookies"}</button>,
        footer,
      )}
      {(choice === "unknown" || open) && createPortal(
        <div style={backdropStyle}>
        <section role="dialog" aria-modal="true" aria-label={german ? "Optionale Google Analytics Messung" : "Optional Google Analytics measurement"} style={panelStyle}>
          <strong>{german ? "Optionale Website-Analyse" : "Optional website analytics"}</strong>
          <p style={{ lineHeight: 1.45, marginBlock: "0.5rem 1rem" }}>{german ? "Mit Ihrer Einwilligung misst Google Analytics Seitenaufrufe und kann Cookies setzen sowie Seiten- und Gerätedaten erhalten. Ohne Einwilligung laden wir den Google-Tag nicht. Sie können Ihre Wahl jederzeit ändern." : "With your permission, Google Analytics measures page views and may set cookies or receive page and device data. We do not load its tag without consent. You can change your choice anytime."} <a href="/datenschutz" style={{ textDecoration: "underline" }}>{german ? "Datenschutzerklärung" : "Privacy policy"}</a>.</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            <button type="button" onClick={() => choose("denied")} style={buttonStyle}>{german ? "Ablehnen" : "Reject"}</button>
            <button type="button" onClick={() => choose("granted")} style={buttonStyle}>{german ? "Analyse erlauben" : "Allow analytics"}</button>
          </div>
        </section>
        </div>, document.body,
      )}
    </>
  );
}

const buttonStyle = { minHeight: 44, padding: "0.5rem 0.875rem", border: "1px solid #5C5E67", borderRadius: "0.5rem", background: "#383A42", color: "#E8E9EC", font: "inherit", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" } as const;
const backdropStyle = { position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", background: "#0008" } as const;
const panelStyle = { width: "min(100%, 27rem)", padding: "1.125rem", border: "1px solid #5C5E67", borderRadius: "0.75rem", background: "#282A32", color: "#E8E9EC", boxShadow: "0 16px 48px #0007", maxHeight: "calc(100dvh - 2rem)", overflowY: "auto", fontSize: "0.8125rem" } as const;
const footerButtonStyle = { border: 0, padding: 0, background: "none", color: "inherit", font: "inherit", textDecoration: "underline", textUnderlineOffset: "0.15em", cursor: "pointer" } as const;
